import {uniq} from 'lodash';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  inject,
  OnDestroy,
  signal,
  ViewChild
} from '@angular/core';
import {
  ClientRequestApiService,
  ClientRequestItemStatus,
  CreateSecureLinkData,
  RequestDocument,
  RequestItem,
  RequestSection,
  SecureLinkGrant,
  SecureLinkUser,
  UpdateSecureLinkGrantsData,
} from '@aiwyn/api';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  DATA_SELECTOR_INJECTION_TOKEN,
  DEFAULT_SNACKBAR_CONFIG,
  DialogSize,
  getControlValueChanges,
  IndeterminateSelectable,
  Selectable,
  SnackbarComponent,
  SnackbarType,
} from '@aiwyn/shared';
import {BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs';
import {distinctUntilChanged, finalize, map, shareReplay, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Store} from '@ngxs/store';
import {ClientRequestsState, CreateSecureLink, UpdateSecureLinkGrants} from '../../../../../../../state';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UNIQUE} from '../../../../../../../../../shared/utils/array';
import {CdkOverlayOrigin} from '@angular/cdk/overlay';
import {cancelRequestAnimationFrame, reqAnimFrame} from '../../../../../../../../../shared/utils/request-animation';
import {AuthService, ContactUser, SecureLinkEntityType} from '@aiwyn/auth';
import {toSignal} from '@angular/core/rxjs-interop';

export interface ReassignModalData {
  requestDocument: RequestDocument;
  requestName$: Observable<string>;
  rawSections$: Observable<RequestSection[]>;
  secureLinkUser: SecureLinkUser;
}

// Object copied from {RequestSection} with items property changed to be selectable
interface eRequestSection {
  id: number;
  name: string;
  ordinal: number;
  items: Selectable<RequestItem>[];
}

@Component({
  templateUrl: './reassign-modal.component.html',
  styleUrl: './reassign-modal.component.scss',
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'reassign-modal',
    },
  ],
  standalone: false,
})
export class ReassignModalComponent implements AfterViewInit, OnDestroy {
  protected readonly DialogSize = DialogSize;
  protected readonly ClientRequestItemStatus = ClientRequestItemStatus;

  private destroy$ = new Subject<void>();
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  public requestName = toSignal(this.data.requestName$);
  private rawSections$ = this.data.rawSections$;

  protected isSubmitting = signal(false);

  sections$: Observable<IndeterminateSelectable<eRequestSection>[]> = this.rawSections$.pipe(
    switchMap(rawSections =>
      combineLatest([of(rawSections), this.selectedItemsId$]),
    ),
    map(([sections, selectedItemsId]) => {
      return sections.map((section) => {
        const mappedItems = section.items
          .map(item => {
            return {
              selected: selectedItemsId.some(id => id === item.id),
              data: item,
            } as Selectable<RequestItem>;
          });

        const availableItems = mappedItems.filter(item => this.isSelectableItemForSection(item));

        const areAllItemsSelected = availableItems.every(item => item.selected) && availableItems.length > 0;
        const isSomeItemSelected = availableItems.some(item => item.selected) && availableItems.length > 0;

        return {
          selected: areAllItemsSelected,
          indeterminate: !areAllItemsSelected && isSomeItemSelected,
          data: {
            ...section,
            items: mappedItems,
          } as eRequestSection,
        } as IndeterminateSelectable<eRequestSection>;
      });
    }),
    shareReplay(1),
  );

  assigneeForm = new FormGroup({
    fname: new FormControl('', [Validators.required]),
    lname: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required]),
    message: new FormControl(''),
  })

  expandedSections: Record<number, boolean> = {}

  requestItems: RequestItem[] = [];

  emails: string[] = [];
  protected emails$: BehaviorSubject<string[]> = new BehaviorSubject([]);

  emailSearch: FormControl<string> = new FormControl<string>('', [Validators.email]);

  availableEmails$ = combineLatest([
    this.emails$,
    getControlValueChanges<string>(this.emailSearch, true),
    getControlValueChanges<string>(this.assigneeForm.controls.email, true),
  ]).pipe(
    map(([emails, searchValue, selectedValue]) => {
      let result = [searchValue, ...emails];

      if (!emails.some(email => email === selectedValue)) {
        result = [searchValue, selectedValue, ...emails];
      }

      return result.filter(email => email && email.includes(searchValue));
    }),
  );

  currentUserGrants: SecureLinkGrant[] = [];

  selectedItemsId: number[] = [];
  selectedItemsId$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

  isLoadingGrants: boolean = false;

  selectedSecureLinkUser!: SecureLinkUser;

  //#region Overlay
  @ViewChild(CdkOverlayOrigin, { static: true, read: ElementRef }) originElement!: ElementRef<HTMLInputElement>;
  triggerWidth: number | null = null;
  isOpen: boolean = false;

  private requestId: number = -1;
  //#endregion

  constructor(
    private readonly clientRequestApiService: ClientRequestApiService,
    private readonly dialogRef: MatDialogRef<ReassignModalComponent>,
    private readonly snackbar: MatSnackBar,
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: ReassignModalData,
  ) { }

  ngAfterViewInit(): void {
    this.rawSections$.pipe(
      take(1),
      tap((sections) => {
        //Created expanded structure
        sections.forEach(section => {
          this.expandedSections[section.id] = true;
        })
      }),
      map((sections) => {
        // Extract requestItems data
        this.requestItems = sections.flatMap(section => section.items);

        return this.requestItems;
      }),
      tap((requestItems) => {
        const emails = requestItems
          .flatMap(item => item.assignees
            .flatMap(assignee => assignee.secureLinkUsers
              .map(secureLinkUser => secureLinkUser.emailAddress),
            ),
          );

        this.emails = UNIQUE(emails);
        this.emails$.next(this.emails);
      }),
      takeUntil(this.destroy$),
    ).subscribe()

    getControlValueChanges<string>(this.assigneeForm.controls.email, true).pipe(
      distinctUntilChanged(),
      tap((value) => {
        this.updateClaims(value);
      }),
      takeUntil(this.destroy$),
    ).subscribe();

    this.populateAssigneeForm(this.data.secureLinkUser);

    if (!this.data.secureLinkUser && this.data.requestDocument.status !== ClientRequestItemStatus.DONE) {
      this.selectedItemsId.push(this.data.requestDocument.id);
      this.selectedItemsId$.next([...this.selectedItemsId]);
    }
  }

  populateAssigneeForm(secureLinkUser: SecureLinkUser): void {
    if (secureLinkUser) {
      this.selectedSecureLinkUser = secureLinkUser;

      const fullName = secureLinkUser.displayName;
      const firstName = fullName.split(' ')[0];
      const lastName = fullName.split(' ').pop();

      this.assigneeForm.controls.fname.setValue(firstName);
      this.assigneeForm.controls.lname.setValue(lastName);
      this.assigneeForm.controls.email.setValue(secureLinkUser.emailAddress);
    } else {
      this.selectedSecureLinkUser = null!;
    }
  }

  expandSection(section: eRequestSection): void {
    this.expandedSections[section.id] = !this.expandedSections[section.id]
  }

  selectItem(requestItem: Selectable<RequestItem>, e?: MouseEvent): void {
    e?.stopPropagation();

    if (!this.isSelectableItemForSection(requestItem)) {
      return;
    }

    if (requestItem.selected) {
      this.selectedItemsId = this.selectedItemsId.filter(selectedFileId => selectedFileId !== requestItem.data.id);
    } else {
      this.selectedItemsId.push(requestItem.data.id);
    }

    this.selectedItemsId$.next([...this.selectedItemsId]);
  }

  selectSection(section: IndeterminateSelectable<eRequestSection>, e?: MouseEvent): void {
    e?.stopPropagation();

    const itemsId = section.data.items.filter( item => this.isSelectableItemForSection(item ))
      .map(item => item.data.id);



    if (section.selected) {
      this.selectedItemsId = this.selectedItemsId.filter(selectedFileId => !itemsId.some(id => id === selectedFileId));
    } else {
      this.selectedItemsId = uniq([...this.selectedItemsId, ...itemsId]);
    }

    this.selectedItemsId$.next([...this.selectedItemsId]);
  }

  updateClaims(emailAddress: string): void {
    if (!emailAddress) {
      return;
    }

    const existingSecureLinksForEmail = this.requestItems.flatMap(requestItem =>
      requestItem.assignees.flatMap(assignee =>
        assignee.secureLinkUsers.filter(secureLinkUser => secureLinkUser.emailAddress === emailAddress),
      ));

    // Update Form and SecureLinkUser when changing email
    if (emailAddress !== this.selectedSecureLinkUser?.emailAddress && existingSecureLinksForEmail.length) {
      const secureLinkUser = existingSecureLinksForEmail[0];
      this.populateAssigneeForm(secureLinkUser);
    }

    // Guarantee values are going to be updated before getting info
    const UUID = this.selectedSecureLinkUser?.uuid;

    if (!UUID) {
      this.selectedSecureLinkUser = null;
      this.assigneeForm.enable();
      return;
    }

    this.isLoadingGrants = true;

    this.clientRequestApiService.getSecureLinksByUUID(
      UUID,
    ).pipe(
      tap(grants => {
        this.selectedItemsId = Array.from(new Set([...this.selectedItemsId, ...this.requestItems
          .filter(requestItem =>
            requestItem.assignees.some(assignee =>
              grants.some(grant => grant.entityId === assignee.id && grant.active),
            ),
          ).map(requestItem => requestItem.id)]));
        this.selectedItemsId$.next(this.selectedItemsId);

        this.currentUserGrants = grants.filter(grant => grant.active);
        this.isLoadingGrants = false;
      }),
    ).subscribe();
  }

  savePermissions(): void {
    this.assigneeForm.updateValueAndValidity();
    this.assigneeForm.markAllAsTouched();

    if (this.assigneeForm.invalid) {
      this.snackbar.openFromComponent(
        SnackbarComponent,
        {
          ...DEFAULT_SNACKBAR_CONFIG,
          data: {
            message: 'Invalid form, please review.',
            type: SnackbarType.ERROR,
          },
        },
      );

      return;
    }

    this.isSubmitting.set(true);

    if (this.selectedSecureLinkUser) {
      this.modifySecureLink();
    } else {
      this.createSecureLink();
    }
  }

  createSecureLink(): void {
    this.assigneeForm.updateValueAndValidity();

    const assigneesId = this.requestItems
      .filter(requestItem => this.selectedItemsId.indexOf(requestItem.id) !== -1)
      .map(requestItem => requestItem.assignees[0].id);
    const rawForm = this.assigneeForm.getRawValue()

    const clientId = this.store.selectSnapshot(ClientRequestsState.getSelectedClientRequest).clientId;

    this.authService.getUserLoadable().pipe(
      switchMap((user) => {
        const contactId = (user.data as ContactUser)?.getContact?.()?.id ?? null;

        const createSecureLinkPayload: CreateSecureLinkData = {
          grants: assigneesId.map(assigneeId => {
            return {
              entityId: assigneeId,
              secureLinkEntityType: SecureLinkEntityType.CLIENT_REQUEST_ITEM_ASSIGNEE,
            };
          }),
          contactId: contactId,
          emailAddress: rawForm.email,
          displayName: `${rawForm.fname} ${rawForm.lname}`,
          message: rawForm.message,
          clientId,
        };

        return this.store.dispatch(new CreateSecureLink(createSecureLinkPayload));
      }),
      tap(() => {
        this.snackbar.openFromComponent(
          SnackbarComponent,
          {
            ...DEFAULT_SNACKBAR_CONFIG,
            data: {
              message: 'Request item successfully reassigned to 3rd party',
              type: SnackbarType.SUCCESS,
            },
          },
        );

        this.onClose();
      }),
      finalize(() => {
        this.isSubmitting.set(false);
      })
    ).subscribe();

  }

  modifySecureLink(): void {
    const assigneesId = this.requestItems
      .filter(requestItem => this.selectedItemsId.indexOf(requestItem.id) !== -1)
      .map(requestItem => requestItem.assignees[0].id);

    const modifiedPreviousGrants = this.currentUserGrants
      .filter(currentUserGrant => !assigneesId.some(assigneeId => assigneeId === currentUserGrant.entityId))
      .map(grant => {
        return {
          ...grant,
          active: false,
        };
      });

    // We need to pass in the existing grants as well, so we can then email all grants
    // later when we send the Secure Links email, otherwise the list of grants when we send
    // the email will be blank, and it will fail on the back end.
    const newlyAddedGrants = assigneesId
      .map((assigneeId) => ({
        id: this.currentUserGrants.find(grant => grant.entityId === assigneeId)?.id || null,
        entityId: assigneeId,
        secureLinkEntityType: SecureLinkEntityType.CLIENT_REQUEST_ITEM_ASSIGNEE,
        active: true,
      }));

    const clientId = this.store.selectSnapshot(ClientRequestsState.getSelectedClientRequest).clientId;

    const rawForm = this.assigneeForm.getRawValue();

    const secureLinkUser: SecureLinkUser = {
      uuid: this.selectedSecureLinkUser.uuid,
      emailAddress: this.assigneeForm.controls.email.value,
      displayName: `${rawForm.fname} ${rawForm.lname}`,
    };

    const payload: UpdateSecureLinkGrantsData = {
      UUID: this.selectedSecureLinkUser.uuid,
      grants: [...modifiedPreviousGrants, ...newlyAddedGrants],
      clientId,
      message: this.assigneeForm.controls.message.value,
      secureLinkUser,
    };

    this.store.dispatch(new UpdateSecureLinkGrants(payload)).pipe(
      tap(() => {
        this.snackbar.openFromComponent(
          SnackbarComponent,
          {
            ...DEFAULT_SNACKBAR_CONFIG,
            data: {
              message: 'Request item successfully reassigned to 3rd party',
              type: SnackbarType.SUCCESS,
            },
          },
        );

        this.onClose();
      }),
      finalize(() => {
        this.isSubmitting.set(false);
      })
    ).subscribe();
  }

  cancelNewValue(): void {
    this.emailSearch.setValue('');
    this.originElement.nativeElement.blur();
  }

  onChangeEmailField(): void {
    if (this.isOpen) {
      return;
    }

    this.confirmNewValue();
  }

  confirmNewValue(email?: string): void {
    if (email) {
      this.emailSearch.setValue(email);
    }

    this.emailSearch.updateValueAndValidity();

    if (this.emailSearch.valid && this.emailSearch.value) {
      this.assigneeForm.controls.email.setValue(this.emailSearch.value);
      this.emailSearch.setValue('');
      this.originElement.nativeElement.blur();
    }

    this.setOpenState(false);
  }

  onClickOutside(_: MouseEvent): void {
    this.setOpenState(false);
    this.confirmNewValue();
  }

  setOpenState(value: boolean): void {
    if (this.isOpen !== value) {
      this.isOpen = value;
      this.onOpenChange();
      this.cdr.markForCheck();
    }
  }

  onOpenChange(): void {
    this.updateCdkConnectedOverlayStatus();
  }

  updateCdkConnectedOverlayStatus(): void {
    if (this.originElement.nativeElement) {
      const triggerWidth = this.triggerWidth;
      cancelRequestAnimationFrame(this.requestId);
      this.requestId = reqAnimFrame(() => {
        // Blink triggers style and layout pipelines anytime the `getBoundingClientRect()` is called, which may cause a
        // frame drop. That's why it's scheduled through the `requestAnimationFrame` to unload the composite thread.
        this.triggerWidth = this.originElement.nativeElement.getBoundingClientRect().width;
        if (triggerWidth !== this.triggerWidth) {
          // The `requestAnimationFrame` will trigger change detection, but we're inside an `OnPush` component which won't have
          // the `ChecksEnabled` state. Calling `markForCheck()` will allow Angular to run the change detection from the root component
          // down to the `nz-select`. But we'll trigger only local change detection if the `triggerWidth` has been changed.
          this.cdr.detectChanges();
        }
      });
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  protected isInSelectableStatus(status: ClientRequestItemStatus): boolean {
    return [ClientRequestItemStatus.TO_DO, ClientRequestItemStatus.OPEN].includes(status);
  }

  protected isSelectableItemForSection(requestItem: Selectable<RequestItem>): boolean {
    if ( !this.isInSelectableStatus(requestItem.data.status) ) {
      return false;
    }
    const hasBeenReassigned = requestItem.data.assignees.some(assignee => assignee.secureLinkUsers.length > 0);
    return !hasBeenReassigned;
  }

  protected isSectionSelectable(section: IndeterminateSelectable<eRequestSection>): boolean {
    return section.data.items.some(item => this.isSelectableItemForSection(item));
  }


  ngOnDestroy(): void {
    cancelRequestAnimationFrame(this.requestId);

    this.destroy$.next();
    this.destroy$.complete();
  }
}
